import { CommonModule } from '@angular/common';
import { Component, ElementRef, NgZone, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { VW_ERROR_CODES } from 'vwappservices';
import { VioTablesV2Module, VT2_TABLE_CONFIG } from 'vio-tables-v2';
import { ProductsApiService } from '../../services/products-api.service';
import { MachinesApiService } from '../../services/machines-api.service';
import { MachineTypesApiService } from '../../services/machine-types-api.service';
import { notifyResult, reportApiError } from '../../shared/api-result.helper';
import { AppendToBodyDirective } from '../../shared/append-to-body.directive';
import { BilingualNameFieldComponent } from '../../shared/bilingual-name-field.component';
import { ConfirmService } from '../../shared/confirm.service';
import { BASE_VIO_TABLE_V2_CONFIG } from '../../shared/vio-table-v2.config';
import { openModalById } from '../../shared/bootstrap-modal.helper';

interface Step {
  _id: string;
  name: string;
  nameMr: string;
  shortName: string;
  shortNameMr: string;
  technicalName: string;
  outputName: string;
  outputNameMr: string;
  description: string;
  sequence: number;
  machineTypeId: string | null;
  machineIds: string[];
  expectedTimeMins: number | null;
  active: boolean;
  dependencyMode: 'strictSequential' | 'dependencyBased' | 'noValidation';
  dependsOn: string[];
}

interface Machine {
  _id: string;
  name: string;
  active: boolean;
  machineTypeId: string | null;
}

interface MachineType {
  _id: string;
  name: string;
}

interface Product {
  _id: string;
  name: string;
  nameMr: string;
  code: string;
  description: string;
  active: boolean;
  monthlyTargets: Record<string, number>;
  steps: Step[];
  trackingMode: string | null;
}

const currentMonthKey = () => new Date().toISOString().slice(0, 7);

// The 12 months of the current calendar year, as "YYYY-MM" keys — the fixed set the Monthly
// Target modal edits at once. Computed once at module load rather than per-call.
const CURRENT_YEAR_MONTH_KEYS: string[] = Array.from(
  { length: 12 },
  (_, i) => `${new Date().getFullYear()}-${String(i + 1).padStart(2, '0')}`,
);
const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

@Component({
  selector: 'app-products',
  standalone: true,
  imports: [CommonModule, FormsModule, AppendToBodyDirective, BilingualNameFieldComponent, VioTablesV2Module],
  templateUrl: './products.component.html',
  styleUrl: './products.component.scss',
})
export class ProductsComponent implements OnInit {
  products: Product[] = [];
  tableData: any[] = [];
  machines: Machine[] = [];
  machineTypes: MachineType[] = [];
  loading = true;

  tableConfig: VT2_TABLE_CONFIG = {
    ...BASE_VIO_TABLE_V2_CONFIG,
    tableTitle: 'Products',
    excelFileName: 'products',
    excludeFields: ['_id'],
    columnLabels: {
      name: 'Name',
      code: 'Code',
      description: 'Description',
      monthlyTarget: "This Month's Target",
      stepCount: 'Steps',
    },
    actionButtons: [
      {
        icon: '✏️',
        label: 'Edit',
        title: 'Edit product',
        cssClass: 'btn btn-outline-primary btn-sm vio-table-icon-btn vio-icon-edit',
        onClick: (row: any) => {
          const product = this.products.find((p) => p._id === row._id);
          if (!product) return;
          this.openEditModal(product);
          openModalById('productModal');
        },
      },
      {
        icon: '📋',
        label: 'Steps',
        title: 'Steps',
        cssClass: 'btn btn-outline-info btn-sm vio-table-icon-btn vio-icon-steps',
        onClick: (row: any) => openModalById('stepsModal-' + row._id),
      },
      {
        icon: '🎯',
        label: 'Monthly Target',
        title: 'Monthly Target',
        cssClass: 'btn btn-outline-success btn-sm vio-table-icon-btn vio-icon-target',
        onClick: (row: any) => {
          const product = this.products.find((p) => p._id === row._id);
          if (!product) return;
          this.openTargetModal(product);
          openModalById('targetModal-' + row._id);
        },
      },
      {
        icon: '🔀',
        label: 'Workflow',
        title: 'Workflow Configuration',
        cssClass: 'btn btn-outline-secondary btn-sm vio-table-icon-btn vio-icon-workflow',
        onClick: (row: any) => {
          const product = this.products.find((p) => p._id === row._id);
          if (!product) return;
          this.openWorkflowModal(product);
          openModalById('workflowModal-' + row._id);
        },
      },
    ],
  };

  newStep = {
    name: '',
    nameMr: '',
    shortName: '',
    shortNameMr: '',
    technicalName: '',
    outputName: '',
    outputNameMr: '',
    description: '',
    machineTypeId: '',
    machineIds: [] as string[],
    expectedTimeMins: null as number | null,
  };

  operation: 'Add' | 'Edit' = 'Add';
  editingId: string | null = null;
  productForm = { name: '', nameMr: '', code: '', description: '', active: true };

  stepOperation: 'Add' | 'Edit' = 'Add';
  editingStepId: string | null = null;
  // Set when "insert after this step" is used (vs. the plain "Add Step" button, which appends
  // at the end) — only meaningful while stepOperation === 'Add'.
  insertAfterStepId: string | null = null;

  // Monthly Target modal — edits the current calendar year's 12 months at once. Save stays
  // disabled until something actually changes (targetFormDirty compares against the snapshot
  // taken when the modal opened).
  targetMonths = CURRENT_YEAR_MONTH_KEYS;
  targetMonthLabels = MONTH_LABELS;
  targetModalProduct: Product | null = null;
  targetForm: Record<string, number> = {};
  private targetFormOriginal: Record<string, number> = {};
  savingTargets = false;

  // Workflow Configuration — one Validation Mode applies to every step in the product at once
  // (simpler than picking a mode per step, which is what this used to be). "Dependency Based" is
  // the one mode that still needs per-step configuration, since which steps depend on which is
  // inherently not a single shared setting.
  workflowMode: 'noValidation' | 'strictSequential' | 'dependencyBased' = 'noValidation';
  readonly workflowModeOptions: { value: 'noValidation' | 'strictSequential' | 'dependencyBased'; label: string; icon: string; description: string }[] = [
    { value: 'noValidation', label: 'No Validation', icon: 'fa-unlock', description: 'Operators can complete any step in any order — no dependency checks are enforced.' },
    {
      value: 'strictSequential',
      label: 'Strict Sequential',
      icon: 'fa-list-ol',
      description: "Each step can only progress once every step before it in the sequence has caught up to the same count for the day.",
    },
    {
      value: 'dependencyBased',
      label: 'Dependency Based',
      icon: 'fa-code-branch',
      description: 'Pick exactly which steps each step must wait on, below — order outside those picks is unrestricted.',
    },
  ];

  @ViewChild('productModalCloseBtn') productModalCloseBtn!: ElementRef<HTMLButtonElement>;

  private toastr = inject(ToastrService);
  private confirmService = inject(ConfirmService);

  constructor(
    private productsApi: ProductsApiService,
    private machinesApi: MachinesApiService,
    private machineTypesApi: MachineTypesApiService,
    private ngZone: NgZone,
  ) {}

  ngOnInit() {
    this.load();
  }

  async load() {
    this.loading = true;
    const [productsRes, machinesRes, machineTypesRes] = await Promise.all([
      this.productsApi.list(),
      this.machinesApi.list(),
      this.machineTypesApi.list(),
    ]);
    // Same zone-continuation issue documented in DashboardComponent/SettingsComponent's load() —
    // without this, `loading` flips to false internally but the view stays stuck.
    this.ngZone.run(() => {
      if (productsRes.errorCode === VW_ERROR_CODES.SUCCESS) {
        // Steps created before machineId was replaced with machineTypeId/machineIds (an array) are
        // still sitting in the DB in their old shape — machineIds missing entirely rather than [].
        // step.machineIds.includes(...) in the template throws on those rows and breaks rendering
        // for the whole list, so normalize once here rather than guarding every call site.
        this.products = (productsRes.Data as Product[]).map((p) => ({
          ...p,
          monthlyTargets: p.monthlyTargets || {},
          steps: p.steps.map((s) => ({
            ...s,
            machineIds: s.machineIds || [],
            machineTypeId: s.machineTypeId || null,
            outputName: s.outputName || '',
            outputNameMr: s.outputNameMr || '',
            shortNameMr: s.shortNameMr || '',
            description: s.description || '',
          })),
        }));
      } else {
        reportApiError(this.toastr, productsRes);
      }
      if (machinesRes.errorCode === VW_ERROR_CODES.SUCCESS) {
        this.machines = machinesRes.Data;
      } else {
        reportApiError(this.toastr, machinesRes);
      }
      if (machineTypesRes.errorCode === VW_ERROR_CODES.SUCCESS) {
        this.machineTypes = machineTypesRes.Data;
      } else {
        reportApiError(this.toastr, machineTypesRes);
      }
      this.tableData = this.products.map((p) => ({
        _id: p._id,
        name: p.nameMr ? `${p.name} (${p.nameMr})` : p.name,
        code: p.code,
        description: p.description || '—',
        monthlyTarget: this.currentMonthTarget(p),
        stepCount: p.steps.length,
      }));
      this.loading = false;
    });
  }

  // Narrows the machine multiselect to whichever machine type is picked first; with none picked,
  // every machine is offered.
  machinesForType(machineTypeId: string | null): Machine[] {
    return machineTypeId ? this.machines.filter((m) => m.machineTypeId === machineTypeId) : this.machines;
  }

  onNewStepMachineTypeChange() {
    this.newStep.machineIds = [];
  }

  toggleNewStepMachine(machineId: string) {
    this.newStep.machineIds = this.newStep.machineIds.includes(machineId)
      ? this.newStep.machineIds.filter((id) => id !== machineId)
      : [...this.newStep.machineIds, machineId];
  }

  // The Add/Edit Step modal swaps in for the Steps modal rather than stacking on top of it — two
  // simultaneously-open Bootstrap modals (each with its own backdrop) got visually muddy and,
  // combined with this app's AppendToBodyDirective relocating modal nodes, left a stray backdrop
  // behind once closed. Hiding one and showing the other keeps exactly one modal/backdrop live.
  openAddStepModal(product: Product, insertAfter?: Step) {
    this.stepOperation = 'Add';
    this.editingStepId = null;
    this.insertAfterStepId = insertAfter ? insertAfter._id : null;
    this.newStep = {
      name: '',
      nameMr: '',
      shortName: '',
      shortNameMr: '',
      technicalName: '',
      outputName: '',
      outputNameMr: '',
      description: '',
      machineTypeId: '',
      machineIds: [],
      expectedTimeMins: null,
    };
    this.hideModal(`stepsModal-${product._id}`);
    this.showModal(`addStepModal-${product._id}`);
  }

  // Same modal as Add, pre-filled — editing a step is no longer done inline in the table (which
  // only shows machines as read-only text now), it goes through this shared form instead.
  openEditStepModal(product: Product, step: Step) {
    this.stepOperation = 'Edit';
    this.editingStepId = step._id;
    this.insertAfterStepId = null;
    this.newStep = {
      name: step.name,
      nameMr: step.nameMr || '',
      shortName: step.shortName || '',
      shortNameMr: step.shortNameMr || '',
      technicalName: step.technicalName || '',
      outputName: step.outputName || '',
      outputNameMr: step.outputNameMr || '',
      description: step.description || '',
      machineTypeId: step.machineTypeId || '',
      machineIds: [...step.machineIds],
      expectedTimeMins: step.expectedTimeMins,
    };
    this.hideModal(`stepsModal-${product._id}`);
    this.showModal(`addStepModal-${product._id}`);
  }

  closeAddStepModal(product: Product) {
    this.hideModal(`addStepModal-${product._id}`);
    this.showModal(`stepsModal-${product._id}`);
  }

  private hideModal(id: string) {
    const el = document.getElementById(id);
    if (el) (window as any).bootstrap.Modal.getOrCreateInstance(el).hide();
  }

  private showModal(id: string) {
    const el = document.getElementById(id);
    if (el) (window as any).bootstrap.Modal.getOrCreateInstance(el).show();
  }

  openAddModal() {
    this.operation = 'Add';
    this.editingId = null;
    this.productForm = { name: '', nameMr: '', code: '', description: '', active: true };
  }

  openEditModal(product: Product) {
    this.operation = 'Edit';
    this.editingId = product._id;
    this.productForm = {
      name: product.name,
      nameMr: product.nameMr || '',
      code: product.code,
      description: product.description,
      active: product.active,
    };
  }

  async saveProduct() {
    if (!this.productForm.name || !this.productForm.code) return;
    const res =
      this.operation === 'Add'
        ? await this.productsApi.create({
            name: this.productForm.name,
            nameMr: this.productForm.nameMr,
            code: this.productForm.code,
            description: this.productForm.description,
          })
        : await this.productsApi.update(this.editingId!, {
            name: this.productForm.name,
            nameMr: this.productForm.nameMr,
            description: this.productForm.description,
            active: this.productForm.active,
          });
    if (!notifyResult(this.toastr, res, `${this.productForm.name} ${this.operation === 'Add' ? 'added' : 'updated'}.`)) return;
    this.productModalCloseBtn.nativeElement.click();
    await this.load();
  }

  currentMonthTarget(product: Product): number {
    return product.monthlyTargets?.[currentMonthKey()] || 0;
  }

  // Unlike Add Step, this modal is opened straight from the Products list (not from within
  // another already-open modal) — data-bs-toggle on the trigger button handles showing it, this
  // just needs to populate the form first.
  openTargetModal(product: Product) {
    this.targetModalProduct = product;
    this.targetForm = {};
    for (const month of this.targetMonths) {
      this.targetForm[month] = product.monthlyTargets?.[month] || 0;
    }
    this.targetFormOriginal = { ...this.targetForm };
  }

  closeTargetModal(product: Product) {
    this.hideModal(`targetModal-${product._id}`);
  }

  get targetFormDirty(): boolean {
    return this.targetMonths.some((month) => this.targetForm[month] !== this.targetFormOriginal[month]);
  }

  get targetYearTotal(): number {
    return this.targetMonths.reduce((sum, month) => sum + (Number(this.targetForm[month]) || 0), 0);
  }

  isCurrentMonth(month: string): boolean {
    return month === currentMonthKey();
  }

  async saveMonthlyTargets(product: Product) {
    if (!this.targetFormDirty) return;
    this.savingTargets = true;
    const res = await this.productsApi.updateMonthlyTargets(product._id, this.targetForm);
    // Same zone-continuation issue documented in DashboardComponent/SettingsComponent's load() —
    // without this, `savingTargets` flips to false internally but the Save button stays stuck.
    this.ngZone.run(() => {
      this.savingTargets = false;
    });
    if (!notifyResult(this.toastr, res, `${product.name} monthly targets updated.`)) return;
    this.closeTargetModal(product);
    await this.load();
  }

  async saveStep(product: Product) {
    if (!this.newStep.name) return;
    const payload: Record<string, any> = { ...this.newStep, machineTypeId: this.newStep.machineTypeId || null };
    if (this.stepOperation === 'Add' && this.insertAfterStepId) payload['insertAfterStepId'] = this.insertAfterStepId;
    const res =
      this.stepOperation === 'Add'
        ? await this.productsApi.addStep(product._id, payload)
        : await this.productsApi.updateStep(product._id, this.editingStepId!, payload);
    if (!notifyResult(this.toastr, res, `Step "${this.newStep.name}" ${this.stepOperation === 'Add' ? 'added' : 'updated'}.`)) return;
    this.closeAddStepModal(product);
    await this.load();
  }

  machineNamesFor(step: Step): string {
    return this.machineNamesForIds(step.machineIds) || '—';
  }

  machineNamesForIds(machineIds: string[]): string {
    if (!machineIds.length) return '';
    return machineIds
      .map((id) => this.machines.find((m) => m._id === id)?.name)
      .filter((name): name is string => !!name)
      .join(', ');
  }

  machineTypeNameFor(step: Step): string {
    if (!step.machineTypeId) return '—';
    return this.machineTypes.find((mt) => mt._id === step.machineTypeId)?.name || '—';
  }

  async deleteStep(product: Product, step: Step) {
    const ok = await this.confirmService.confirm({
      message: `Remove step "${step.name}"? This cannot be undone.`,
      confirmText: 'Remove',
      danger: true,
    });
    if (!ok) return;
    const res = await this.productsApi.deleteStep(product._id, step._id);
    if (!notifyResult(this.toastr, res, `${step.name} removed.`)) return;
    await this.load();
  }

  async moveStep(product: Product, step: Step, direction: -1 | 1) {
    const sorted = this.sortedSteps(product);
    const idx = sorted.findIndex((s) => s._id === step._id);
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    [sorted[idx], sorted[swapIdx]] = [sorted[swapIdx], sorted[idx]];
    const res = await this.productsApi.resequenceSteps(
      product._id,
      sorted.map((s) => s._id),
    );
    if (!notifyResult(this.toastr, res, 'Step order updated.')) return;
    await this.load();
  }

  // Initializes the centralized mode from whatever's currently on the steps — existing products
  // saved before this was centralized may have a mix, so this just picks the first step's mode as
  // a reasonable starting point rather than forcing everyone back to "No Validation".
  openWorkflowModal(product: Product) {
    this.workflowMode = product.steps[0]?.dependencyMode ?? 'noValidation';
  }

  async saveWorkflow(product: Product) {
    const res = await this.productsApi.updateWorkflow(product._id, {
      trackingMode: product.trackingMode || null,
      steps: product.steps.map((s) => ({
        stepId: s._id,
        dependencyMode: this.workflowMode,
        // dependsOn only means anything in Dependency Based mode — clear it otherwise so a step
        // doesn't carry stale, invisible dependencies from before a mode switch.
        dependsOn: this.workflowMode === 'dependencyBased' ? s.dependsOn : [],
      })),
    });
    if (!notifyResult(this.toastr, res, 'Workflow saved.')) return;
    await this.load();
  }

  toggleDependsOn(step: Step, otherStepId: string) {
    const idx = step.dependsOn.indexOf(otherStepId);
    if (idx === -1) step.dependsOn.push(otherStepId);
    else step.dependsOn.splice(idx, 1);
  }

  sortedSteps(product: Product): Step[] {
    return [...product.steps].sort((a, b) => a.sequence - b.sequence);
  }

  // Reloading re-fetches `products` as a brand-new array, so without trackBy Angular treats every
  // row as removed+re-added by identity — including the per-product Steps modal element itself
  // while it's open. That tears the open modal out of the DOM mid-use without giving Bootstrap a
  // chance to run its own close/cleanup, orphaning its backdrop and freezing the page. Tracking by
  // _id lets Angular reuse the existing DOM node across reloads instead.
  trackByProductId(_index: number, product: Product): string {
    return product._id;
  }
}
